import { useEffect, useRef, useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'react-toastify'

// Components
import { CrudNavigation } from '../components/CrudNavigation'
import { ProcessosTable } from '../components/ProcessosTable'
import { SetoresTable } from '../components/SetoresTable'
import { AutorizadoresTable } from '../components/AutorizadoresTable'
import { SistemsTable } from '../components/SistemsTable'
import { ProcessesForm } from '../components/ProcessesForm'
import { SectorForm } from '../components/SectorForm'
import { TeamForm } from '../components/TeamForm'
import { SistemsForm } from '../components/SistemsForm'
import { ManageUsersModal } from '../components/ViewModal/ManageUsersModal'
import { ViewModal, type ViewModalType } from '../components/ViewModal'
import { Loading } from '../../../components'

// Hooks
import { useCrudLogic } from '../hooks/useCrudLogic'
import { apiService } from '../../demands/services/api'

// Types
import type { User, Processo, Setor, Autorizador, Sistema, CreateSistemaRequest } from '../../../models'
import { getSetorType } from '../../../models'
import type { CreateProcessoRequest } from '../components/ProcessesForm/schemas'
import type { TemplateSchema } from '../../templates'

// Tipo para autorizadores que agora são setores
type SetorAutorizador = {
  id: number
  nome_setor: string
  equipe?: boolean
  chefe_setor_fk?: number | null
  active?: boolean
  pivot?: {
    processo_fk: number
    autorizador_fk: number
    ordem: number
    active: boolean
    created_at?: string
    updated_at?: string
  }
}

// Interface para conversão
interface InitialProcessData {
  nome_processo?: string
  tipo?: string
  prioridade_baixa?: number
  prioridade_media?: number
  prioridade_alta?: number
  prioridade_urgente?: number
  setor_processo_fk?: number
  descricao?: string
  instrucoes?: string
  links?: Array<{link: string; titulo?: string}> | string
  autorizacoes?: Array<{
    autorizador_fk: number
    ordem: number | null
    active?: boolean
  }>
  anexos?: Array<{
    anexo: string
    nome_anexo?: string
  }>
  gestor_demandante?: boolean
  gestor_demandado?: boolean
  executivo_demandante?: boolean
  executivo_demandado?: boolean
  equipe?: boolean // Pode ser aberto por equipe
  template_schema?: TemplateSchema | null // Schema do template selecionado
}

export const CrudPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSetor, setSelectedSetor] = useState<number | null>(null)
  const hasLoadedUserRef = useRef(false)

  // Determinar o tipo atual baseado na URL
  const getCurrentTypeFromPath = (): 'processos' | 'setores' | 'equipes' | 'autorizadores' | 'sistemas' => {
    const path = location.pathname
    if (path.includes('/setores')) return 'setores'
    if (path.includes('/equipes')) return 'equipes'
    if (path.includes('/autorizadores')) return 'autorizadores'
    if (path.includes('/sistemas')) return 'sistemas'
    return 'processos'
  }

  // Determinar o modo atual baseado na URL
  const getCurrentModeFromPath = (): 'list' | 'create' | 'edit' => {
    const path = location.pathname
    if (path.includes('/edit')) return 'edit'
    if (path.includes('/new')) return 'create'
    return 'list'
  }

  // Estado do ViewModal
  const [viewModal, setViewModal] = useState<{
    isOpen: boolean
    type: ViewModalType | null
    item: Processo | Setor | Autorizador | Sistema | null
  }>({
    isOpen: false,
    type: null,
    item: null
  })

  // Carregar usuário atual
  useEffect(() => {
    if (hasLoadedUserRef.current) {
      return
    }

    hasLoadedUserRef.current = true

    const loadCurrentUser = async () => {
      try {
        const user = await apiService.getUsuarioAtual()
        setCurrentUser(user)
      } catch (error) {
        console.error('Erro ao carregar usuário atual:', error)
        toast.error('Erro ao carregar informações do usuário')
      } finally {
        setIsLoadingUser(false)
      }
    }

    loadCurrentUser()
  }, [])

  const currentType = getCurrentTypeFromPath()
  const currentMode = getCurrentModeFromPath()

  const logic = useCrudLogic({ 
    currentUser, 
    currentType, 
    currentMode,
    searchQuery,
    selectedSetor
  })

  // Redirecionar usuário para rota permitida se tentar acessar uma rota sem permissão
  useEffect(() => {
    if (isLoadingUser || !currentUser) return
    
    const { permissions } = logic
    
    // Verificar se o usuário tem permissão para o tipo atual
    const hasPermissionForType = () => {
      switch (currentType) {
        case 'processos':
          return permissions.canViewProcessos
        case 'setores':
          return permissions.canViewSetores
        case 'equipes':
          return permissions.canViewEquipes
        case 'autorizadores':
          return permissions.canViewAutorizadores
        case 'sistemas':
          return permissions.canViewProcessos // Sistemas reutiliza permissão de processos
        default:
          return false
      }
    }

    // Verificar permissão para o modo atual
    const hasPermissionForMode = () => {
      if (currentMode === 'list') return true // Listar é controlado pela permissão de visualização
      
      // Criar e editar precisam de permissões específicas
      switch (currentType) {
        case 'processos':
          return permissions.canCreateProcessos || permissions.canEditProcessos
        case 'setores':
          return permissions.canCreateSetores || permissions.canEditSetores
        case 'equipes':
          return permissions.canCreateEquipes || permissions.canEditEquipes
        case 'autorizadores':
          return permissions.canCreateAutorizadores || permissions.canEditAutorizadores
        case 'sistemas':
          return permissions.canCreateProcessos || permissions.canEditProcessos // Sistemas reutiliza permissão de processos
        default:
          return false
      }
    }

    if (!hasPermissionForType()) {
      // Redirecionar para a primeira rota permitida
      if (permissions.canViewProcessos) {
        navigate('/gestao/processos', { replace: true })
      } else {
        // Se não tem permissão para nada, vai mostrar a tela de acesso negado
        return
      }
    } else if ((currentMode === 'create' || currentMode === 'edit') && !hasPermissionForMode()) {
      // Se tentou criar/editar sem permissão, volta para a lista
      navigate(`/gestao/${currentType}`, { replace: true })
    }
  }, [isLoadingUser, currentUser, currentType, currentMode, logic.permissions, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Recuperar item do estado da navegação quando entrar no modo de edição
  useEffect(() => {
    if (currentMode === 'edit' && location.state?.editItem) {
      logic.setEditingItem(location.state.editItem)
    }
  }, [currentMode, location.state]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sincronizar tipo e modo quando a URL mudar
  useEffect(() => {
    logic.handleTypeChange(currentType)
  }, [currentType]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    logic.handleModeChange(currentMode)
  }, [currentMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Função para converter Processo em InitialProcessData
  const convertProcessoToInitialData = (processo: Processo): InitialProcessData => {
    const result: InitialProcessData = {
      nome_processo: processo.nome_processo,
      prioridade_baixa: processo.prioridade_baixa,
      prioridade_media: processo.prioridade_media,
      prioridade_alta: processo.prioridade_alta,
      prioridade_urgente: processo.prioridade_urgente,
      setor_processo_fk: processo.setor_processo_fk,
      descricao: processo.descricao,
      gestor_demandante: !!processo.gestor_demandante,
      gestor_demandado: !!processo.gestor_demandado,
      executivo_demandante: !!processo.executivo_demandante,
      executivo_demandado: !!processo.executivo_demandado,
      equipe: processo.equipe !== undefined ? !!processo.equipe : true, // Por padrão true
    }
    
    if (processo.tipo) {
      result.tipo = processo.tipo
    }
    
    if (processo.instrucoes) {
      result.instrucoes = processo.instrucoes
    }
    
    if (processo.links) {
      result.links = processo.links
    }
    
    if (processo.autorizacoes) {
      result.autorizacoes = processo.autorizacoes.map(auth => ({
        autorizador_fk: auth.autorizador_fk,
        ordem: auth.ordem || null,
        active: true
      }))
    }
    
    if (processo.anexos) {
      result.anexos = processo.anexos.map(anexo => ({
        anexo: anexo.anexo,
        nome_anexo: anexo.nome_anexo
      }))
    }
    
    // Incluir template_schema se existir
    if (processo.template_schema) {
      result.template_schema = processo.template_schema as TemplateSchema
    }
    
    return result
  }

  // Handlers do ViewModal
  const handleView = (item: Processo | Setor | Autorizador | Sistema, type: ViewModalType) => {
    setViewModal({ isOpen: true, type, item })
  }

  const handleCloseViewModal = () => {
    setViewModal({ isOpen: false, type: null, item: null })
  }

  const handleEditFromView = () => {
    if (!viewModal.item) return

    // Salvar item para edição
    logic.setEditingItem(viewModal.item)

    // Fechar modal de visualização
    handleCloseViewModal()

    // Navegar para rota de edição com estado
    switch (viewModal.type) {
      case 'processos':
        navigate('/gestao/processos/edit', { state: { editItem: viewModal.item } })
        break
      case 'setores':
        navigate('/gestao/setores/edit', { state: { editItem: viewModal.item } })
        break
      case 'equipes':
        navigate('/gestao/equipes/edit', { state: { editItem: viewModal.item } })
        break
      case 'autorizadores':
        navigate('/gestao/autorizadores/edit', { state: { editItem: viewModal.item } })
        break
      case 'sistemas':
        navigate('/gestao/sistemas/edit', { state: { editItem: viewModal.item } })
        break
    }
  }

  const handleDeleteFromView = async () => {
    if (!viewModal.item) return

    try {
      switch (viewModal.type) {
        case 'processos':
          await logic.deleteProcesso(viewModal.item as Processo)
          break
        case 'setores':
        case 'equipes':
          await logic.deleteSetor(viewModal.item as Setor)
          break
        case 'autorizadores':
          await logic.deleteAutorizador(viewModal.item as Autorizador)
          break
        case 'sistemas':
          await logic.deleteSistema(viewModal.item as Sistema)
          break
      }
      // Fechar modal após deletar
      handleCloseViewModal()
    } catch (error) {
      console.error('Erro ao deletar item:', error)
    }
  }

  // Navegação entre entidades nos modais
  const handleNavigateToProcesso = (processo: Processo) => {
    setViewModal({ isOpen: true, type: 'processos', item: processo })
  }

  const handleNavigateToSetor = (setor: Setor) => {
    setViewModal({ isOpen: true, type: getSetorType(setor) === 'equipe' ? 'equipes' : 'setores', item: setor })
  }

  const handleNavigateToAutorizador = (autorizador: Autorizador | SetorAutorizador) => {
    setViewModal({ isOpen: true, type: 'autorizadores', item: autorizador as Autorizador })
  }

  // Handlers para mudança de tipo e modo via navegação
  const handleTypeChange = (type: 'processos' | 'setores' | 'equipes' | 'autorizadores' | 'sistemas') => {
    navigate(`/gestao/${type}`)
  }

  const handleModeChange = (mode: 'list' | 'create' | 'edit') => {
    const basePath = `/gestao/${currentType}`
    if (mode === 'list') {
      navigate(basePath)
    } else if (mode === 'create') {
      navigate(`${basePath}/new`)
    } else if (mode === 'edit') {
      navigate(`${basePath}/edit`)
    }
  }

  // Handlers locais para edição que navegam corretamente
  const handleEditProcesso = (processo: Processo) => {
    logic.setEditingItem(processo)
    navigate('/gestao/processos/edit', { state: { editItem: processo } })
  }

  const handleEditSetor = (setor: Setor) => {
    logic.setEditingItem(setor)
    const isSetorEquipe = getSetorType(setor) === 'equipe'
    const path = isSetorEquipe ? '/gestao/equipes/edit' : '/gestao/setores/edit'
    navigate(path, { state: { editItem: setor } })
  }

  const handleEditAutorizador = (autorizador: Autorizador) => {
    logic.setEditingItem(autorizador)
    navigate('/gestao/autorizadores/edit', { state: { editItem: autorizador } })
  }

  const handleEditSistema = (sistema: Sistema) => {
    logic.setEditingItem(sistema)
    navigate('/gestao/sistemas/edit', { state: { editItem: sistema } })
  }

  // Extrair setores únicos para o filtro
  const setoresDisponiveis = useMemo(() => {
    const setoresMap = new Map<number, Setor>()
    logic.processos.forEach(processo => {
      if (processo.setor && !setoresMap.has(processo.setor.id)) {
        setoresMap.set(processo.setor.id, processo.setor)
      }
    })
    return Array.from(setoresMap.values()).sort((a, b) => a.nome_setor.localeCompare(b.nome_setor))
  }, [logic.processos])

  // Limpar busca e filtro quando trocar de tipo
  useEffect(() => {
    setSearchQuery('')
    setSelectedSetor(null)
  }, [logic.currentType])

  // Loading inicial
  if (isLoadingUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loading message="Carregando..." />
          <p className="text-gray-600 dark:text-gray-400">Aguarde enquanto verificamos suas permissões</p>
        </div>
      </div>
    )
  }

  // Se usuário não tem permissão para ver nada
  if (!logic.permissions.canViewProcessos && !logic.permissions.canViewSetores && !logic.permissions.canViewEquipes && !logic.permissions.canViewAutorizadores) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="inline-flex p-4 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">Acesso Negado</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Você não possui permissões para acessar esta seção. Entre em contato com um administrador para obter as permissões necessárias.
          </p>
          <button 
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-700 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    )
  }

  const renderContent = () => {
    // Modo de criação/edição
    if (logic.currentMode === 'create' || logic.currentMode === 'edit') {
      const isEdit = logic.currentMode === 'edit' && logic.editingItem

      switch (logic.currentType) {
        case 'processos': {
          const processFormProps = {
            // Usar todosSetores para garantir que tanto setores quanto equipes apareçam no select
            setores: logic.todosSetores,
            onSubmit: isEdit 
              ? (data: CreateProcessoRequest | FormData) => logic.updateProcesso(logic.editingItem!.id, data)
              : logic.createProcesso,
            onCancel: () => handleModeChange('list'),
            isLoading: logic.isLoadingTodosSetores,
            isEdit: !!isEdit
          }
          
          if (isEdit && logic.editingItem) {
            (processFormProps as typeof processFormProps & { initialData: InitialProcessData }).initialData = convertProcessoToInitialData(logic.editingItem as Processo)
          }
          
          return <ProcessesForm {...processFormProps} />
        }
        
        case 'setores':
          return (
            <SectorForm
              usuarios={logic.usuarios}
              onSubmit={isEdit 
                ? (data) => logic.updateSetor(logic.editingItem!.id, data)
                : logic.createSetor
              }
              isLoading={logic.isLoadingUsuarios}
              initialData={isEdit && logic.editingItem ? logic.editingItem as Setor : undefined}
              isEdit={!!isEdit}
            />
          )
        
        case 'equipes':
          return (
            <TeamForm
              usuarios={logic.usuarios}
              onSubmit={isEdit 
                ? (data) => logic.updateSetor(logic.editingItem!.id, { ...data, equipe: true })
                : logic.createEquipe
              }
              isLoading={logic.isLoadingUsuarios}
              initialData={isEdit && logic.editingItem ? logic.editingItem as Setor : undefined}
              isEdit={!!isEdit}
            />
          )
        
        case 'sistemas': {
          const sistemaFormProps = {
            usuarios: logic.usuarios,
            onSubmit: isEdit 
              ? (data: CreateSistemaRequest) => logic.updateSistema(logic.editingItem!.id, data)
              : logic.createSistema,
            isLoading: logic.isLoadingUsuarios,
            isEdit: !!isEdit,
          }
          if (isEdit && logic.editingItem) {
            (sistemaFormProps as typeof sistemaFormProps & { initialData: Sistema }).initialData = logic.editingItem as Sistema
          }
          return <SistemsForm {...sistemaFormProps} />
        }
        
        //case 'autorizadores':
          // return (
          //   <AutorizadorForm
          //     usuarios={logic.usuarios}
          //     onSubmit={isEdit 
          //       ? (data) => logic.updateAutorizador(logic.editingItem!.id, data)
          //       : logic.createAutorizador
          //     }
          //     isLoading={logic.isLoadingUsuarios}
          //     initialData={isEdit && logic.editingItem ? logic.editingItem as Autorizador : undefined}
          //     isEdit={!!isEdit}
          //   />
          // )
        
        default:
          return null
      }
    }

    // Modo de listagem
    switch (logic.currentType) {
      case 'processos':
        return (
          <ProcessosTable
            processos={logic.processos}
            permissions={logic.permissions}
            isLoading={logic.isLoadingProcessos}
            onEdit={handleEditProcesso}
            onDelete={logic.deleteProcesso}
            onView={(processo) => handleView(processo, 'processos')}
            searchQuery={searchQuery}
            selectedSetor={selectedSetor}
          />
        )
      
      case 'setores':
        return (
          <SetoresTable
            setores={logic.setores}
            permissions={logic.permissions}
            isLoading={logic.isLoadingSetores}
            showEquipes={false}
            onEdit={handleEditSetor}
            onDelete={logic.deleteSetor}
            onView={(setor) => handleView(setor, 'setores')}
            onManageUsers={logic.handleManageUsers}
            searchQuery={searchQuery}
          />
        )
      
      case 'equipes':
        return (
          <SetoresTable
            setores={logic.setores}
            permissions={logic.permissions}
            isLoading={logic.isLoadingSetores}
            showEquipes={true}
            onEdit={handleEditSetor}
            onDelete={logic.deleteSetor}
            onView={(setor) => handleView(setor, 'equipes')}
            onManageUsers={logic.handleManageUsers}
            searchQuery={searchQuery}
          />
        )
      
      case 'autorizadores':
        return (
          <AutorizadoresTable
            autorizadores={logic.autorizadores}
            permissions={logic.permissions}
            isLoading={logic.isLoadingAutorizadores}
            onEdit={handleEditAutorizador}
            onDelete={logic.deleteAutorizador}
            onView={(autorizador) => handleView(autorizador, 'autorizadores')}
            searchQuery={searchQuery}
          />
        )
      
      case 'sistemas':
        return (
          <SistemsTable
            sistemas={logic.sistemas}
            permissions={{
              canCreate: logic.permissions.canCreateProcessos,
              canEdit: logic.permissions.canEditProcessos,
              canDelete: logic.permissions.canDeleteProcessos,
              canView: logic.permissions.canViewProcessos,
            }}
            isLoading={logic.isLoadingSistemas}
            onEdit={handleEditSistema}
            onDelete={logic.deleteSistema}
            onView={(sistema) => handleView(sistema, 'sistemas')}
            searchQuery={searchQuery}
            usuarios={logic.usuarios}
            setores={logic.todosSetores}
          />
        )
      
      default:
        return null
    }
  }

  return (
    <div className="w-full overflow-hidden flex flex-col h-[calc(100vh-103px)]">
      <div className="w-full mx-auto flex flex-col flex-1 overflow-hidden">
        
        {/* Navigation */}
        <div className="flex-shrink-0 mb-6">
          <CrudNavigation
            currentType={logic.currentType}
            currentMode={logic.currentMode}
            permissions={logic.permissions}
            onTypeChange={handleTypeChange}
            onModeChange={handleModeChange}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={
              logic.currentType === 'processos' ? 'Buscar processos...' :
              logic.currentType === 'setores' ? 'Buscar setores...' :
              logic.currentType === 'equipes' ? 'Buscar equipes...' :
              logic.currentType === 'sistemas' ? 'Buscar sistemas...' :
              'Buscar autorizadores...'
            }
            setoresDisponiveis={setoresDisponiveis}
            selectedSetor={selectedSetor}
            onSetorChange={setSelectedSetor}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${logic.currentType}-${logic.currentMode}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{
                duration: 0.3,
                ease: "easeInOut"
              }}
              className="h-full"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Modal de Gerenciamento de Usuários */}
        <ManageUsersModal
          isOpen={logic.manageUsersModal.isOpen}
          setor={logic.manageUsersModal.setor}
          allUsers={logic.usuarios}
          onClose={logic.handleCloseManageUsers}
          onVincularUsuarios={logic.vincularUsuariosSetor}
          onDesvincularUsuario={logic.desvincularUsuarioSetor}
          onRefresh={logic.refreshSetorData}
        />

        {/* Modal de Visualização */}
        <ViewModal
          isOpen={viewModal.isOpen}
          onClose={handleCloseViewModal}
          type={viewModal.type}
          item={viewModal.item}
          onEdit={handleEditFromView}
          onDelete={handleDeleteFromView}
          canEdit={
            viewModal.type === 'processos' ? logic.permissions.canEditProcessos :
            viewModal.type === 'setores' ? logic.permissions.canEditSetores :
            viewModal.type === 'equipes' ? logic.permissions.canEditEquipes :
            viewModal.type === 'sistemas' ? logic.permissions.canEditProcessos :
            viewModal.type === 'autorizadores' ? logic.permissions.canEditAutorizadores :
            false
          }
          canDelete={
            viewModal.type === 'processos' ? logic.permissions.canDeleteProcessos :
            viewModal.type === 'setores' ? logic.permissions.canDeleteSetores :
            viewModal.type === 'equipes' ? logic.permissions.canDeleteEquipes :
            viewModal.type === 'sistemas' ? logic.permissions.canDeleteProcessos :
            viewModal.type === 'autorizadores' ? logic.permissions.canDeleteAutorizadores :
            false
          }
          // Navegação entre entidades
          onNavigateToProcesso={handleNavigateToProcesso}
          onNavigateToSetor={handleNavigateToSetor}
          onNavigateToAutorizador={handleNavigateToAutorizador}
        />

      </div>
    </div>
  )
}

